// Recently-viewed items live in localStorage, scoped by browse context
// (pre-owned vs defected, etc.) so each listing page shows relevant history.

const STORAGE_KEY = 'flea:recentlyViewed';
const MAX_ITEMS = 10;

export const RECENTLY_VIEWED_SCOPES = {
  REGULAR: 'regular_items',
  DEFECTED: 'defected_items',
  DESIGNER: 'designer_items',
  BOUTIQUE: 'boutique_items',
};

const safeParse = (raw) => {
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

/** Derive browse scope from a listing payload. */
export function resolveRecentlyViewedScope(item) {
  if (!item) return RECENTLY_VIEWED_SCOPES.REGULAR;
  if (item.is_defected === true || item.is_defected === 'true') {
    return RECENTLY_VIEWED_SCOPES.DEFECTED;
  }
  const listingType = String(item.listing_type || item.line_kind || '').toLowerCase();
  if (listingType.includes('boutique')) return RECENTLY_VIEWED_SCOPES.BOUTIQUE;
  if (listingType.includes('designer')) return RECENTLY_VIEWED_SCOPES.DESIGNER;
  return RECENTLY_VIEWED_SCOPES.REGULAR;
}

function normalizeScope(scope) {
  return scope || RECENTLY_VIEWED_SCOPES.REGULAR;
}

function matchesScope(entry, scope) {
  return normalizeScope(entry.scope) === normalizeScope(scope);
}

export function getRecentlyViewed(scope) {
  if (typeof window === 'undefined') return [];
  const all = safeParse(window.localStorage.getItem(STORAGE_KEY));
  if (!scope) return all;
  return all.filter((entry) => matchesScope(entry, scope));
}

export function addRecentlyViewed(item) {
  if (typeof window === 'undefined' || !item || !item.id) return;
  const scope = resolveRecentlyViewedScope(item);
  const slim = {
    id: item.id,
    title: item.title,
    price: item.price,
    primary_image: item.primary_image || item.image_url || null,
    category: item.category,
    condition: item.condition || null,
    brand_name: item.brand_name || null,
    custom_brand_name: item.custom_brand_name || null,
    is_no_brand: item.is_no_brand ?? null,
    item_code: item.item_code,
    is_defected: Boolean(item.is_defected),
    scope,
    viewed_at: Date.now(),
  };
  const existing = getRecentlyViewed().filter((entry) => entry.id !== item.id);
  const next = [slim, ...existing].slice(0, MAX_ITEMS);
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next.slice(0, 5)));
    } catch {
      /* give up silently */
    }
  }
}

export function clearRecentlyViewed(scope) {
  if (typeof window === 'undefined') return;
  if (!scope) {
    window.localStorage.removeItem(STORAGE_KEY);
    return;
  }
  const remaining = getRecentlyViewed().filter((entry) => !matchesScope(entry, scope));
  if (remaining.length) {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(remaining));
  } else {
    window.localStorage.removeItem(STORAGE_KEY);
  }
}
